// @flow

/* This is needed because the modal should be a div even though it's clickable */
/* eslint react/no-did-mount-set-state:0 */
/* eslint jsx-a11y/no-static-element-interactions:0 */

import React, { Component } from 'react';
import i18n from 'app/common/lib/i18n';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import { compose } from 'redux';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import withSeoExperiments, { type SeoExperiments } from 'app/common/lib/react/withSeoExperiments';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Icon, IconButton } from 'gestalt';

export const ModalType = {
  DEFAULT: 'default',
  CLOSEUP: 'closeup',
  FULL_WIDTH: 'fullWidth',
};

type OwnProps = {|
  canClose: boolean,
  hidden: boolean,
  hybridTier: string,
  onCloseClick: () => void,
  onHide: () => void,
  renderContent: (scrollContainer: ?HTMLElement, handleCloseClick: ?() => void) => void,
  type: $Values<typeof ModalType>,
|};

type Props = {|
  ...OwnProps,
  requestContext: RequestContextType,
  seoExperiments: SeoExperiments,
  seoUnauthExperiments: UnauthSeoExperiments,
|};

type State = {|
  hidden: boolean,
  scrollContainer: ?HTMLElement,
|};

const styles = {
  mask: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    opacity: 1,
    position: 'fixed',
    top: '0px',
    bottom: '0px',
    left: '0px',
    right: '0px',
    zIndex: 1000,
  },
  closeupMask: {
    backgroundColor: 'rgba(239, 239, 239, 0.96)',
    cursor: 'zoom-out',
    opacity: 1,
  },
  scroller: {
    WebkitOverflowScrolling: 'touch',
    overflowY: 'scroll',
    bottom: '0px',
    left: '0px',
    right: '0px',
    top: '0px',
    position: 'fixed',
    WebkitBoxOrient: 'vertical',
    WebkitBoxDirection: 'normal',
    WebkitFlexDirection: 'column',
    msFlexDirection: 'column',
    flexDirection: 'column',
    WebkitJustifyContent: 'space-around',
    msFlexPack: 'distribute',
    justifyContent: 'space-around',
  },
  modalContainer: {
    height: '100%',
    display: 'flex',
    minHeight: '0px',
    position: 'relative',
    opacity: 1,
    borderRadius: '6px',
    margin: '0 auto',
  },
  fullWidthModalContainer: {
    margin: '0 0',
  },
  closeButton: {
    backgroundColor: 'Transparent',
    backgroundRepeat: 'no-repeat',
    border: 'none',
    outline: 'none',
    overflow: 'hidden',
    padding: '10px',
    position: 'fixed',
    right: '24px',
    top: '24px',
  },
  closeButtonWithCloseupHeader: {
    position: 'fixed',
    right: '20px',
    top: '72px',
  },
  closeButtonForImageOnly: {
    position: 'fixed',
    right: '24px',
    top: '24px',
  },
  arrowLeft: { __style: { left: 0, top: 350 } },
  arrowRight: { __style: { right: 0, top: 350 } },
};

const ESC_KEYCODE = 27;

export class FullPageModal extends Component<Props, State> {
  static defaultProps = {
    canClose: false,
    hidden: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      hidden: props.hidden,
      scrollContainer: null,
    };
  }

  componentDidMount() {
    // Because the full page modal can be presented on top of a scrolling window,
    // children may need to know to listen on this container, for new scroll events.
    this.setState({
      scrollContainer: this.fullPageModal,
    });

    window.addEventListener('keydown', this.handleKeyDown);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    this.setState({ hidden: nextProps.hidden });
  }

  fullPageModal: ?HTMLElement;

  handleKeyDown = (evt: SyntheticKeyboardEvent<HTMLInputElement>) => {
    const { type, canClose } = this.props;

    if (evt.keyCode === ESC_KEYCODE) {
      trackRegisterAction(
        `unauth.authentication_modal.esc_key_click.NOT_NOW_BUTTON.${type}.${
          canClose ? 'can' : 'cant'
        }_close`,
      );
      if (canClose) {
        trackRegisterAction(`unauth.authentication_modal.esc_key_click.DISMISSED.${type}`);
        this.handleCloseClick();
      }
    }
    evt.stopPropagation();
  };

  handleCloseClick = () => {
    const { canClose, onCloseClick } = this.props;

    if (canClose && onCloseClick) {
      onCloseClick();
      return;
    }

    this.hide();
  };

  handleContentClick = (event: SyntheticMouseEvent<>) => {
    // do not let this event propagate and close the modal
    event.stopPropagation();
  };

  handleContentKeyDown = (event: SyntheticKeyboardEvent<>) => {
    if (this.props.canClose && event.keyCode === ESC_KEYCODE) {
      event.stopPropagation();
      this.handleCloseClick();
    }
  };

  handleCloseButtonClick = () => {
    const { type } = this.props;
    if (type === ModalType.CLOSEUP) {
      this.trackScopedAction('close_button_click');
    } else {
      trackRegisterAction(`unauth.authentication_modal.x_button_click.NOT_NOW_BUTTON.${type}`);
    }
    this.handleCloseClick();
  };

  handleEmptySpaceClick = () => {
    this.trackScopedAction('empty_space_click');

    if (this.props.canClose) {
      const inWashDismissExp = new SingleTreatmentExp(
        this.props.requestContext,
        'unauth_desktop_modal_wash_click_dismiss',
      ).enabled();

      if (inWashDismissExp) {
        this.handleCloseClick();
      }
    }
  };

  trackScopedAction = (action: string) => {
    trackRegisterAction(`unauth.full_page_modal.${this.props.type}.${action}`);
  };

  hide() {
    if (this.props.canClose) {
      this.setState({ hidden: true });
      if (this.props.onHide) {
        this.props.onHide();
      }
    }
  }

  customMaskStyle(type: $Values<typeof ModalType>) {
    const mask = Object.assign({}, styles.mask);
    if (type === ModalType.CLOSEUP) {
      Object.assign(mask, styles.closeupMask);
    }
    return mask;
  }

  shouldRenderCloseButton = () => this.props.canClose;

  renderCloseButton() {
    const { type } = this.props;
    if (type === ModalType.CLOSEUP) {
      const iconStyle =
        type === ModalType.CLOSEUP
          ? styles.closeButtonWithCloseupHeader
          : styles.closeButtonForImageOnly;
      return (
        <div style={iconStyle}>
          <IconButton
            accessibilityLabel={i18n._('close', 'accessible label for close button')}
            icon="cancel"
            onClick={this.handleCloseButtonClick}
            size="sm"
          />
        </div>
      );
    }
    return (
      <button
        aria-label={i18n._('close', 'accessible label for close button')}
        onClick={this.handleCloseButtonClick}
        style={styles.closeButton}
      >
        <Icon
          accessibilityLabel={i18n._('close', 'accessible label for close button')}
          color={'white'}
          icon="cancel"
          inline
        />
      </button>
    );
  }

  render() {
    if (this.state.hidden) {
      return null;
    }

    const { renderContent, type } = this.props;

    const maskStyle = this.customMaskStyle(type);
    let modalContainerStyle = Object.assign({}, styles.modalContainer);

    if (type === ModalType.FULL_WIDTH) {
      modalContainerStyle = { ...modalContainerStyle, ...styles.fullWidthModalContainer };
    }

    // handleCloseClick is optionally passed down to renderContent in cases where
    // children need to explicitly define where the close click event is fired
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    return (
      <div style={maskStyle}>
        <div
          className="FullPageModal__scroller"
          // Clicking outside of the content in a modal usually has a different action (ie: closing the modal)
          onClick={this.handleEmptySpaceClick}
          ref={(ref) => {
            this.fullPageModal = ref;
          }}
          role="dialog"
          style={styles.scroller}
        >
          <div
            onClick={this.handleContentClick}
            onKeyDown={this.handleContentKeyDown}
            role="dialog"
            style={modalContainerStyle}
          >
            {renderContent(this.state.scrollContainer, this.handleCloseClick)}
            {this.shouldRenderCloseButton() ? this.renderCloseButton() : null}
          </div>
          {/* eslint-enable */}
        </div>
      </div>
    );
  }
}

export default compose(
  withRequestContext,
  withSeoExperiments,
  withUnauthSeoExperiments,
)(FullPageModal);
